/**
 * Web Speech API engine — used by Vite PWA.
 */
import type { SpeechEngine, SpeakOptions, SpeechVoice } from './speech.types';

export type {
  SpeechEngine,
  SpeakOptions,
  SpeechVoice,
  SpeechPauseMode,
} from './speech.types';

function getSynth(): SpeechSynthesis | null {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    return null;
  }
  return window.speechSynthesis;
}

let paused = false;

export const speechEngine: SpeechEngine = {
  pauseMode: 'restart',
  runtime: 'web',

  async getAvailableVoices(): Promise<SpeechVoice[]> {
    const synth = getSynth();
    if (!synth) return [];

    const mapVoices = (): SpeechVoice[] =>
      synth.getVoices().map((voice, index) => ({
        identifier: voice.voiceURI || `${voice.lang}-${index}`,
        language: voice.lang,
        name: voice.name,
      }));

    const existing = mapVoices();
    if (existing.length > 0) return existing;

    await new Promise<void>((resolve) => {
      const onVoices = () => {
        synth.removeEventListener('voiceschanged', onVoices);
        resolve();
      };
      synth.addEventListener('voiceschanged', onVoices);
      setTimeout(() => {
        synth.removeEventListener('voiceschanged', onVoices);
        resolve();
      }, 500);
    });
    return mapVoices();
  },

  speak(text: string, options: SpeakOptions = {}) {
    const synth = getSynth();
    if (!synth || !text.trim()) {
      options.onError?.();
      return;
    }

    synth.cancel();
    paused = false;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = options.language ?? 'pl-PL';
    utterance.rate = options.rate ?? 1;
    utterance.pitch = options.pitch ?? 1;

    if (options.voice) {
      const match = synth
        .getVoices()
        .find(
          (voice) =>
            voice.voiceURI === options.voice || voice.name === options.voice
        );
      if (match) utterance.voice = match;
    }

    utterance.onstart = () => options.onStart?.();
    utterance.onend = () => {
      if (paused) return;
      options.onDone?.();
    };
    utterance.onerror = () => {
      if (paused) return;
      options.onError?.();
    };

    synth.speak(utterance);
  },

  async stop() {
    const synth = getSynth();
    paused = false;
    synth?.cancel();
  },

  async pause() {
    const synth = getSynth();
    if (!synth) return;
    paused = true;
    if (typeof synth.pause === 'function') {
      synth.pause();
      return;
    }
    synth.cancel();
  },

  async resume() {
    const synth = getSynth();
    if (!synth) return;
    paused = false;
    if (typeof synth.resume === 'function' && synth.paused) {
      synth.resume();
    }
  },
};
